import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Blocks, BrainCircuit, Bot, Workflow, HardHat, BookOpen } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";

const SITE = "https://oceandata.ca";

const SERVICES = [
  {
    icon: Blocks,
    title: "Zoho CRM + Zoho Projects, implemented for your trade",
    body: "Deal won → project created → milestones tracked → invoice ready. We implement Zoho CRM and Zoho Projects around how construction and professional-services firms actually run — RFIs, change orders, progress billing — not around a generic template.",
  },
  {
    icon: BookOpen,
    title: "Zoho Books without the month-end scramble",
    body: "Estimates, retainers, payment reminders, bank feeds, project and timesheet billing — configured so \"what can we invoice right now?\" is a glance, not an investigation. One client watched payment cycles shrink from 45 days to 12.",
  },
  {
    icon: BrainCircuit,
    title: "Zoho MCP setup with Claude — 27 purpose-built tools",
    body: "We connect Claude to your Zoho through the Model Context Protocol: pipeline and forecast, leads and follow-up, project health, sales-to-delivery. Ask your business a question in plain words; get the answer from your live records. Every write waits for a human yes and lands in Zoho's audit trail.",
  },
  {
    icon: Bot,
    title: "Zoho Zia or Claude? Honest guidance.",
    body: "Zia is Zoho's built-in assistant — handy inside single apps. When you need cross-app reasoning (a question that spans CRM, Projects, and Books at once), Claude through MCP goes deeper. We implement Zia where it fits and add Claude where it counts. No hype, just the right tool per job.",
  },
  {
    icon: Workflow,
    title: "Already running n8n workflows? Keep them.",
    body: "We're tool-agnostic by design. If your automations already run on n8n, Make, or Zapier, we connect them into the same source of truth rather than ripping them out. Your workflows keep working; they just finally share one record.",
  },
  {
    icon: HardHat,
    title: "Autodesk + construction AI, linked to the job",
    body: "Plans, models, and site documents in Autodesk Construction Cloud, tied to the milestones and RFIs your team tracks in Zoho. The site and the office read the same truth — decisions in hours, not days (43% faster RFI response, measured at Construction Horizon).",
  },
];

const FAQ = [
  {
    q: "Are you a certified Zoho partner in Montréal?",
    a: "Yes — Zoho Premium ecosystem experience with six Zoho certifications, including Zoho CRM Certified Administrator, and 647 Zoho One implementations since 2017. Ocean Data Inc. is based at 75 Rue Queen Ste 1400, Montréal, serving Québec and Canada; Ocean Data LLC serves Arizona and South Florida from Phoenix. We work in French, English, and Spanish.",
  },
  {
    q: "What is Zoho MCP and why does it matter?",
    a: "MCP (Model Context Protocol) is how Claude reads and acts inside your Zoho apps safely. Our setup exposes 27 purpose-built tools — so answers come from your live deals, projects, and invoices, and every action asks before it writes. It's the difference between a chatbot demo and a copilot you can trust.",
  },
  {
    q: "Do you replace our existing tools or connect them?",
    a: "Connect. Excel, QuickBooks, Microsoft 365, Google, n8n, Autodesk — whatever you already run stays. We close the gaps between tools; we don't sell migrations.",
  },
  {
    q: "How do we start?",
    a: "A free 15-minute review: you show us where the hours go, we show you the two or three connections worth making first. Then one process, a fixed price, and 30 free days on your real data before any decision.",
  },
];

export default function ZohoIntegrationsPage() {
  const { setOpen } = useSimplify();

  useEffect(() => {
    document.title = "Zoho Partner Montréal — CRM, Books, Projects, MCP + Claude, Zia, n8n, Autodesk | Ocean Data";
    const el = document.createElement("link");
    el.rel = "canonical";
    el.href = `${SITE}/zoho-integrations-montreal`;
    el.setAttribute("data-hreflang-injected", "true");
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE}/zoho-integrations-montreal`,
        name: "Zoho integrations in Montréal — CRM, Books, Projects, MCP + Claude, Zia, n8n, Autodesk",
        description:
          "Ocean Data is a Zoho partner in Montréal connecting Zoho CRM, Zoho Books and Zoho Projects with Claude AI via Zoho MCP (27 tools), guiding Zoho Zia adoption, connecting existing n8n workflows, and linking Autodesk construction files — one source of truth, 647 implementations since 2017.",
        inLanguage: "en-CA",
        isPartOf: { "@id": `${SITE}/#website` },
        about: { "@id": `${SITE}/#org` },
      },
      {
        "@type": "Service",
        name: "Zoho implementation and AI integration — Montréal",
        provider: { "@id": `${SITE}/#org` },
        areaServed: [
          { "@type": "City", name: "Montréal" },
          { "@type": "AdministrativeArea", name: "Québec" },
          { "@type": "City", name: "Miami" },
        ],
        description:
          "Zoho CRM, Zoho Books and Zoho Projects implementation; Zoho MCP setup with Claude; Zoho Zia guidance; n8n workflow connection; Autodesk integration for construction firms.",
      },
      {
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]">
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            Zoho Partner · Montréal — Miami · 647 implementations since 2017
          </p>
          <h1
            data-testid="zoho-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="zoho-headline-line-1">Zoho CRM, Books & Projects —</MaskedLine>
            <MaskedLine delay={0.3} testId="zoho-headline-line-2">
              <span className="text-brand-teal">connected, with Claude inside.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            One source of truth for construction and professional-services
            firms: your Zoho apps, your existing tools, and a quiet AI copilot
            that asks before it writes. Measured capacity back — not another
            software project.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <button
              data-testid="zoho-cta-hero"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              Show me where I can simplify
              <ArrowRight size={18} />
            </button>
            <p className="text-sm text-slate-500">Free 15-minute review · No commitment</p>
          </motion.div>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            What we connect
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Every piece of your Zoho stack — and everything around it.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7">
            {SERVICES.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div
                  data-testid={`zoho-service-${i}`}
                  className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-7 sm:p-8 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <span className="w-10 h-10 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center mb-4">
                    <s.icon size={17} />
                  </span>
                  <h3 className="font-display font-semibold text-xl text-brand-navy leading-snug mb-3">{s.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Proof band */}
      <section className="py-16 lg:py-24 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold mb-10">
            Measured, not promised
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              ["647", "Zoho One implementations since 2017"],
              ["25–40 %", "less administrative time, measured across client engagements"],
              ["27", "purpose-built MCP tools connecting Claude to your Zoho"],
            ].map(([num, label], i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div data-testid={`zoho-proof-${i}`}>
                  <p className="font-display font-bold text-4xl sm:text-5xl text-white mb-2">{num}</p>
                  <p className="text-sm text-brand-pale leading-relaxed">{label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-10 text-sm text-slate-400 max-w-2xl">
              See the method:{" "}
              <Link to="/how-it-works" className="text-brand-gold hover:text-white transition-colors font-semibold">
                how the 30 free days work
              </Link>
              {" · "}
              <Link to="/journal/horizon-construction-case-study" className="text-brand-gold hover:text-white transition-colors font-semibold">
                the Horizon Construction case study
              </Link>
              {" · "}
              <Link to="/pricing" className="text-brand-gold hover:text-white transition-colors font-semibold">
                simple pricing
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Questions fréquentes / FAQ
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-12">
            Asked before every engagement
          </h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <details data-testid={`zoho-faq-${i}`} className="group rounded-2xl bg-white border border-slate-200 px-7 py-6 open:border-brand-teal transition-colors">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-display font-semibold text-lg text-brand-navy">
                    {f.q}
                    <span className="text-brand-teal text-xl transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="rounded-3xl bg-brand-navy text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div className="max-w-xl">
                <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight mb-4">
                  Fifteen minutes. Your stack. An honest answer.
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Show us how your tools run today — Zoho or not — and we'll
                  show you the two or three connections worth making first.
                </p>
              </div>
              <div className="flex flex-col items-start gap-4 shrink-0">
                <button
                  data-testid="zoho-cta-final"
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
                >
                  Request my free review
                  <ArrowRight size={18} />
                </button>
                <p className="text-xs text-slate-400">
                  <a href="tel:+15414469447" className="text-brand-gold hover:text-white transition-colors">+1.541.446.9447</a>
                  {" · "}Montréal — Miami
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
